/**
 * HQ services between missions (04 §2.4, §4.4): a replacement recruit for RP, and Priority
 * Medevac for a ranger in hospital. The `GameApi` contract has no method for either; the real
 * implementation takes them through `visitSupportNode` under reserved ids - `hq:recruit` and
 * `hq:medevac:<rangerId>` - and describes `hq:recruit` / `hq:medevac` (name, and the price in the
 * text). The HQ button only appears when the API describes them, so another API simply lacks it.
 *
 * HQ is also where a perk made teachable by recovered tags is handed on (04 §2.3): TRAIN A PERK,
 * through `GameApi.trainPerk` (trainDialog.ts).
 */

import '../widgets/dossier.css';
import type { Operation } from '../../../meta/types';
import type { Ctx } from '../context';
import { box, button, el, errorMessage } from '../dom';
import { isDescribed, serviceName } from '../text';
import { dialog, notice } from '../widgets/confirm';
import { openTrainDialog, trainablePerks } from './trainDialog';

export const HQ_RECRUIT = 'hq:recruit';
export const HQ_MEDEVAC = 'hq:medevac';

/** The id a ranger's medevac is requested under. */
export function medevacId(rangerId: string): string {
  return `${HQ_MEDEVAC}:${rangerId}`;
}

/** The API offers the HQ services (it describes them). */
export function hqServices(ctx: Ctx): boolean {
  const raw = (id: string): { name: string; text: string } => ctx.api.describe(id);
  return isDescribed(raw, HQ_RECRUIT) || isDescribed(raw, HQ_MEDEVAC);
}

/** There is something to do at HQ: a service to request, or a perk to teach. */
export function hqAvailable(ctx: Ctx, op: Operation): boolean {
  return trainablePerks(op).length > 0 || hqServices(ctx);
}

/** The map's button: a perk waiting to be taught is the news, so it takes the label. */
export function hqLabel(op: Operation): string {
  return trainablePerks(op).length > 0 ? 'HQ: TRAIN A PERK' : 'HQ: RECRUIT / MEDEVAC';
}

/** Opens the HQ dialog; `onResolved` runs after the player has read the result of a request. */
export function openHqDialog(ctx: Ctx, op: Operation, onResolved: () => void): void {
  const d = dialog(`HQ - RP ${op.rp}`, () => ctx.closeModal(), 'menu-dialog-wide');
  const error = el('div', 'menu-error menu-hidden');

  const request = (id: string, title: string): void => {
    let line: string;
    try {
      line = ctx.api.visitSupportNode(id, 0);
    } catch (err) {
      error.textContent = errorMessage(err);
      error.classList.remove('menu-hidden');
      return;
    }
    notice(ctx, title, [line === '' ? 'DONE.' : line], onResolved);
  };

  let first: HTMLButtonElement | null = null;
  const perks = trainablePerks(op);
  if (perks.length > 0) {
    d.body.append(el('div', 'menu-dossier-key', 'TRAIN A PERK - RECOVERED TAGS MADE THESE TEACHABLE, ONE RANGER EACH'));
    const row = box('menu-row menu-row-wrap');
    for (const perk of perks) {
      const about = ctx.describe(perk);
      const b = button(`TRAIN: ${about.name}`, () => openTrainDialog(ctx, op, perk, onResolved), 'menu-btn-small');
      b.title = about.text;
      row.append(b);
      first ??= b;
    }
    d.body.append(row);
  }

  if (hqServices(ctx)) {
    const recruit = ctx.describe(HQ_RECRUIT);
    const recruitButton = button('REQUEST A REPLACEMENT', () => request(HQ_RECRUIT, recruit.name), 'menu-btn-small');
    d.body.append(box('menu-row menu-row-wrap', el('span', 'menu-prose menu-grow', recruit.text), recruitButton));
    first ??= recruitButton;

    const medevac = ctx.describe(HQ_MEDEVAC);
    const inHospital = op.company.filter((r) => r.status === 'wounded' || r.status === 'critical');
    d.body.append(el('div', 'menu-prose', medevac.text));
    if (inHospital.length === 0) d.body.append(el('div', 'menu-dim', 'NOBODY IS IN HOSPITAL.'));
    for (const r of inHospital) {
      const label = `MEDEVAC ${serviceName(r)} (OUT ${Math.max(0, r.recoveryMissions)})`;
      d.body.append(box('menu-row', button(label, () => request(medevacId(r.id), medevac.name), 'menu-btn-small')));
    }
  }
  d.body.append(error);

  const close = button('CLOSE', () => ctx.closeModal());
  d.actions.append(close);
  ctx.openModal({ ...d.modal, initialFocus: () => first ?? close });
}
